/**
 * track — first-party product events for the top of the funnel.
 *
 * WHY: the backend can reconstruct everything from signup onwards (see
 * `GET /admin/funnel`), but nothing can see what happens BEFORE a Firebase
 * account exists. After the 2026-08-16 launch there was no way to tell whether
 * thirty people had clicked the link and bounced or three people had clicked —
 * two diagnoses with opposite fixes. These events close that gap.
 *
 * Deliberately not an analytics SDK: no third party, no cookies, no consent
 * banner, and nothing about anyone's portfolio ever leaves for a vendor. Events
 * go to our own `POST /public/event`, which accepts a closed vocabulary only.
 *
 * Contract: this module NEVER throws and NEVER blocks. Every call is
 * fire-and-forget. If tracking breaks, the app must not notice.
 */
import { auth } from "@/firebase";

const BASE_URL = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, "");

/** Mirrors tools/event_log.ALLOWED_EVENTS — the server drops anything else. */
export type TrackEvent =
  | "landing_view"
  | "signin_view"
  | "signup_click"
  | "role_selected"
  | "paywall_view"
  | "plan_click"
  // Task outcomes (F32). Everything above is acquisition — how someone
  // arrives. These say whether the product then worked for them.
  | "import_saved"
  | "import_failed"
  | "report_reopen_failed"
  | "topup_unconfirmed"
  | "panel_error";

type Props = Record<string, string | number | boolean | undefined>;

const ANON_KEY = "ss_anon_id";
const ATTR_KEY = "ss_attribution";

/** A random id so we can count PEOPLE, not page loads. Not a cookie, not shared
 *  across browsers, and carries no identity of its own. */
function anonId(): string {
  try {
    let id = localStorage.getItem(ANON_KEY);
    if (!id) {
      id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `a${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
      localStorage.setItem(ANON_KEY, id);
    }
    return id;
  } catch {
    return ""; // private mode / storage blocked — still send, just uncounted
  }
}

/**
 * First-touch attribution, captured once and reused. Stored on first arrival so
 * a signup three days later is still credited to the campaign that produced it.
 */
function attribution(): Props {
  try {
    const saved = localStorage.getItem(ATTR_KEY);
    if (saved) return JSON.parse(saved) as Props;
    const q = new URLSearchParams(window.location.search);
    const attr: Props = {};
    const src = q.get("utm_source") || q.get("ref");
    if (src) attr.source = src;
    const med = q.get("utm_medium");
    if (med) attr.medium = med;
    const camp = q.get("utm_campaign");
    if (camp) attr.campaign = camp;
    if (!attr.source && document.referrer) {
      try {
        const host = new URL(document.referrer).hostname;
        if (host && host !== window.location.hostname) attr.source = host;
      } catch {
        /* malformed referrer — ignore */
      }
    }
    localStorage.setItem(ATTR_KEY, JSON.stringify(attr));
    return attr;
  } catch {
    return {};
  }
}

/**
 * Record one event. Fire-and-forget: never awaited, never throws, never
 * surfaces an error to the caller.
 *
 * `sendBeacon` is preferred because it survives the page unload that follows a
 * sign-in redirect — which is exactly when `signup_click` fires, and precisely
 * the event a normal fetch would lose.
 */
export function track(event: TrackEvent, props: Props = {}): void {
  try {
    if (!BASE_URL) return;
    const payload = JSON.stringify({
      event,
      anon_id: anonId(),
      props: { ...attribution(), path: window.location.pathname, ...props },
    });
    const url = `${BASE_URL}/public/event`;

    if (typeof navigator !== "undefined" && typeof navigator.sendBeacon === "function") {
      navigator.sendBeacon(url, new Blob([payload], { type: "application/json" }));
      return;
    }
    void fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* telemetry must never surface */
    });
  } catch {
    /* telemetry must never surface */
  }
}

/**
 * Same as `track`, but attaches the Firebase token so the server can tie the
 * event to a uid. Use for post-auth events (paywall_view, plan_click) where
 * knowing WHICH user hit the wall is the whole point.
 *
 * Falls back to an anonymous send if no token is available, so an event is
 * never lost just because auth was still settling.
 */
export function trackAuthed(event: TrackEvent, props: Props = {}): void {
  try {
    if (!BASE_URL) return;
    const u = auth.currentUser;
    if (!u) {
      track(event, props);
      return;
    }
    void u
      .getIdToken()
      .then((token) =>
        fetch(`${BASE_URL}/public/event`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            event,
            anon_id: anonId(),
            props: { ...attribution(), path: window.location.pathname, ...props },
          }),
          keepalive: true,
        }),
      )
      .catch(() => {
        /* telemetry must never surface */
      });
  } catch {
    /* telemetry must never surface */
  }
}

/** Fire an event at most once per browser — for view events that would
 *  otherwise re-fire on every React re-render or route re-entry. */
const fired = new Set<string>();
export function trackOnce(event: TrackEvent, props: Props = {}): void {
  const key = `${event}:${JSON.stringify(props)}`;
  if (fired.has(key)) return;
  fired.add(key);
  track(event, props);
}
